import React, { useEffect, useState } from "react";
import axios from "axios";
import "../css/dcrLeaveType.css";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import LeavesHistory from "../Admin/LeavesHistory";
import Toaster, {
  showSuccessToast,
  showErrorToast,
  showErrorWithDetailsToast,
  dismissToast,
} from "../dist/Toaster";
import { hasAccess } from "../utils/access";

const DCRLeaveTypePage = () => {
  const today = new Date().toISOString().split("T")[0];
  const navigate = useNavigate();
  const [divisions, setDivisions] = useState([]);
  const isExpenseUser = hasAccess("dashboard_expense");
  const [states, setStates] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [actionMode, setActionMode] = useState("apply");
  const [allSubStates, setAllSubStates] = useState([]); // full list from /api/sub_states
  const [subStates, setSubStates] = useState([]); // filtered by selected states
  const [selectedSubStates, setSelectedSubStates] = useState([]);
  const [selectedDivisions, setSelectedDivisions] = useState([]);
  const [selectedStates, setSelectedStates] = useState([]);
  const [selectedDesignations, setSelectedDesignations] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [leaveMode, setLeaveMode] = useState("single");
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [leaveType, setLeaveType] = useState("");
  const [message, setMessage] = useState("");
  const [applyResponse, setApplyResponse] = useState(null);
  const [showSkippedModal, setShowSkippedModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorDetails, setErrorDetails] = useState(null);
  const [divisionCounts, setDivisionCounts] = useState({});
  const [stateCounts, setStateCounts] = useState({});
  const [designationCounts, setDesignationCounts] = useState({});
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const isCoordinator = hasAccess("dashboard_coordinator");

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [divRes, stateRes, subStateRes, desigRes, empRes] =
          await Promise.all([
            axios.get(`${BASE_URL}/api/divisions`, {
              headers: getAuthHeaders(),
            }),
            axios.get(`${BASE_URL}/api/states`, { headers: getAuthHeaders() }),
            axios.get(`${BASE_URL}/api/sub_states`, {
              headers: getAuthHeaders(),
            }),
            axios.get(`${BASE_URL}/api/designations/`, {
              headers: getAuthHeaders(),
            }),
            axios.get(`${BASE_URL}/api/employees?status=working`, {
              headers: getAuthHeaders(),
            }),
          ]);

        setDivisions(divRes.data);
        setStates(stateRes.data);
        setDesignations(desigRes.data);
        setEmployees(empRes.data);

        // Use substate API response directly — it has { id, name, state_id }
        // state_id here matches emp.state_id in the employee object (both numbers)
        setAllSubStates(subStateRes.data);
        setSubStates(subStateRes.data); // show all initially

        const divCount = {};
        const stateCount = {};
        const desigCount = {};

        empRes.data.forEach((emp) => {
          // emp.division      = string name e.g. "WELLNESS"  → key for divisionCounts
          // emp.state_id      = number e.g. 37              → key for stateCounts
          // emp.designation_id = number e.g. 2              → key for designationCounts
          divCount[emp.division] = (divCount[emp.division] || 0) + 1;
          stateCount[emp.state_id] = (stateCount[emp.state_id] || 0) + 1;
          desigCount[emp.designation_id] =
            (desigCount[emp.designation_id] || 0) + 1;
        });

        setDivisionCounts(divCount);
        setStateCounts(stateCount);
        setDesignationCounts(desigCount);
      } catch (err) {
        console.error("Error fetching data:", err);
        showErrorToast("Failed to load initial data");
      }
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    if (leaveMode === "single") {
      setEndDate(startDate);
    }
  }, [leaveMode, startDate]);

  useEffect(() => {
    axios
      .get(`${BASE_URL}/api/dcr-leave-types`, { headers: getAuthHeaders() })
      .then((res) => setLeaveTypes(res.data))
      .catch((err) => {
        console.error("Failed to load leave types", err);
        showErrorToast("Failed to load leave types");
      });
  }, []);

  // When selected states change:
  // 1. Reset selected substates
  // 2. Filter substate list to only show substates belonging to selected states
  //    (substate API returns state_id which matches emp.state_id)
  useEffect(() => {
    setSelectedSubStates([]);
    if (selectedStates.length === 0) {
      setSubStates(allSubStates);
    } else {
      const filtered = allSubStates.filter((ss) =>
        selectedStates.includes(ss.state_id),
      );
      setSubStates(filtered);
    }
  }, [selectedStates, allSubStates]);

  // Confirmed field names from actual API response:
  // emp.division       → string  e.g. "WELLNESS"   — matches d.name in divisions
  // emp.state_id       → number  e.g. 37            — matches s.id in states list
  // emp.substate_id    → number  e.g. 52            — matches ss.id in sub_states list
  // emp.designation_id → number  e.g. 2             — matches d.id in designations list
  const filteredEmployees = employees.filter((emp) => {
    const matchDivision =
      selectedDivisions.length === 0 ||
      selectedDivisions.includes(emp.division);

    const matchState =
      selectedStates.length === 0 || selectedStates.includes(emp.state_id);

    const matchSubState =
      selectedSubStates.length === 0 ||
      selectedSubStates.includes(emp.substate_id);

    const matchDesignation =
      selectedDesignations.length === 0 ||
      selectedDesignations.includes(emp.designation_id);

    return matchDivision && matchState && matchSubState && matchDesignation;
  });

  const handleCheckboxChange = (value, selectedList, setList) => {
    setList((prev) =>
      prev.includes(value) ? prev.filter((i) => i !== value) : [...prev, value],
    );
  };

  const handleSelectAll = (items, selectedList, setList, key) => {
    if (selectedList.length === items.length) {
      setList([]);
    } else {
      const values = items.map((item) => {
        if (key === "division") return item.name;
        if (key === "state") return item.id;
        if (key === "designation") return item.id;
        if (key === "employee") return item.id;
        if (key === "substate") return item.id;
        return item.id;
      });
      setList(values);
    }
  };

  const validateInputs = () => {
    if (!startDate) {
      showErrorToast("Please select a date");
      return false;
    }
    if (selectedEmployees.length === 0) {
      showErrorToast("Please select at least one employee");
      return false;
    }
    if (actionMode === "apply" && !leaveType) {
      showErrorToast("Please select leave type");
      return false;
    }
    if (actionMode === "delete" && !leaveType) {
      showErrorToast("Please select leave type to delete");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateInputs()) return;

    setApplyResponse(null);
    setShowSkippedModal(false);
    setErrorDetails(null);

    try {
      const selectedLeaveType = leaveTypes.find(
        (type) => type.name === leaveType,
      );

      if (!selectedLeaveType) {
        showErrorToast("Invalid leave type selected");
        return;
      }

      // ================= APPLY =================
      if (actionMode === "apply") {
        const assignments = selectedEmployees.map((empId) => ({
          start_date: startDate,
          end_date: leaveMode === "single" ? startDate : endDate,
          leave_type_id: selectedLeaveType.id,
          date: startDate,
          employee_id: empId,
          remarks: message,
        }));

        const response = await axios.post(
          `${BASE_URL}/api/dcr-leave-assignments/bulk-application`,
          assignments,
          { headers: getAuthHeaders() },
        );

        setApplyResponse(response.data);

        const { message: backendMessage, skipped_count = 0 } = response.data;

        if (skipped_count > 0) {
          showSuccessToast({
            title: "Leave Processing Completed",
            message: `${backendMessage} | Skipped: ${skipped_count}`,
            button: {
              label: "View Skipped Details",
              onClick: () => {
                setShowSkippedModal(true);
                dismissToast();
              },
            },
          });
        } else {
          showSuccessToast({
            title: "Success",
            message: backendMessage,
          });
          setHistoryRefreshKey((prev) => prev + 1);
        }
      }

      // ================= DELETE =================
      if (actionMode === "delete") {
        const deletePayload = selectedEmployees.map((empId) => ({
          employee_id: empId,
          leave_type_id: selectedLeaveType.id,
          date: startDate,
          end_date: leaveMode === "single" ? startDate : endDate,
        }));

        const response = await axios.post(
          `${BASE_URL}/api/dcr-leave-assignments/bulk-delete`,
          deletePayload,
          { headers: getAuthHeaders() },
        );

        showSuccessToast({
          title: "Delete Successful",
          message:
            response.data?.message || "Leave records deleted successfully.",
        });
        setHistoryRefreshKey((prev) => prev + 1);
      }
    } catch (error) {
      console.error("API Error:", error);

      const errorResponse = error.response?.data;
      const backendMessage =
        errorResponse?.detail ||
        errorResponse?.message ||
        errorResponse?.error ||
        error.message ||
        "Something went wrong";

      if (
        errorResponse?.errors ||
        errorResponse?.failed_assignments ||
        errorResponse?.details
      ) {
        setErrorDetails({
          message: backendMessage,
          errors:
            errorResponse.errors ||
            errorResponse.failed_assignments ||
            errorResponse.details,
          status: error.response?.status,
        });

        showErrorWithDetailsToast(backendMessage, errorResponse, () =>
          setShowErrorModal(true),
        );
      } else {
        showErrorToast(backendMessage);
      }
    }
  };

  return (
    <div className="dcr-leave-type-main-container">
      {/* Toast Container - Place it once at the top level */}
      <Toaster />

      <div className="p-2 border-2 border-gray-300 rounded-lg bg-white">
        <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
          <span className="text-blue-700">DCR </span>
          <span className="text-blue-500">Type</span>
        </h1>

        <div className="dcr-leave-type-filter-container">
          <div className="dcr-leave-type-division-section">
            <label className="dcr-leave-type-label-checkbox">
              <span>Division</span>
              <input
                type="checkbox"
                checked={
                  selectedDivisions.length === divisions.length &&
                  divisions.length > 0
                }
                onChange={() =>
                  handleSelectAll(
                    divisions,
                    selectedDivisions,
                    setSelectedDivisions,
                    "division",
                  )
                }
              />
            </label>
            <div className="dcr-leave-type-division-list">
              {divisions.map((d) => (
                <div key={d.id} className="dcr-leave-type-checkbox-item">
                  <input
                    type="checkbox"
                    checked={selectedDivisions.includes(d.name)}
                    onChange={() =>
                      handleCheckboxChange(
                        d.name,
                        selectedDivisions,
                        setSelectedDivisions,
                      )
                    }
                  />
                  {d.name} ({divisionCounts[d.name] || 0})
                </div>
              ))}
            </div>
          </div>

          <div className="dcr-leave-type-state-section">
            <label className="dcr-leave-type-label-checkbox">
              <span>State</span>
              <input
                type="checkbox"
                checked={
                  selectedStates.length === states.length && states.length > 0
                }
                onChange={() =>
                  handleSelectAll(
                    states,
                    selectedStates,
                    setSelectedStates,
                    "state",
                  )
                }
              />
            </label>
            <div className="dcr-leave-type-state-list">
              {states.map((s) => (
                <div key={s.id} className="dcr-leave-type-checkbox-item">
                  <input
                    type="checkbox"
                    checked={selectedStates.includes(s.id)}
                    onChange={() =>
                      handleCheckboxChange(
                        s.id,
                        selectedStates,
                        setSelectedStates,
                      )
                    }
                  />
                  {/* stateCounts keyed by state_id (number) — matches s.id */}
                  {s.name} ({stateCounts[s.id] || 0})
                </div>
              ))}
            </div>
          </div>

          <div className="dcr-leave-type-state-section">
            <label className="dcr-leave-type-label-checkbox">
              <span>Sub State</span>
              <input
                type="checkbox"
                checked={
                  selectedSubStates.length === subStates.length &&
                  subStates.length > 0
                }
                onChange={() =>
                  handleSelectAll(
                    subStates,
                    selectedSubStates,
                    setSelectedSubStates,
                    "substate",
                  )
                }
              />
            </label>
            {/* subStates is already pre-filtered by selectedStates via useEffect above */}
            <div className="dcr-leave-type-state-list">
              {subStates.map((s) => (
                <div key={s.id} className="dcr-leave-type-checkbox-item">
                  <input
                    type="checkbox"
                    checked={selectedSubStates.includes(s.id)}
                    onChange={() =>
                      handleCheckboxChange(
                        s.id,
                        selectedSubStates,
                        setSelectedSubStates,
                      )
                    }
                  />
                  {s.name}
                </div>
              ))}
            </div>
          </div>

          <div className="dcr-leave-type-designation-section">
            <label className="dcr-leave-type-label-checkbox">
              <span>Designation</span>
              <input
                type="checkbox"
                checked={
                  selectedDesignations.length === designations.length &&
                  designations.length > 0
                }
                onChange={() =>
                  handleSelectAll(
                    designations,
                    selectedDesignations,
                    setSelectedDesignations,
                    "designation",
                  )
                }
              />
            </label>
            <div className="dcr-leave-type-designation-list">
              {designations.map((d) => (
                <div key={d.id} className="dcr-leave-type-checkbox-item">
                  <input
                    type="checkbox"
                    checked={selectedDesignations.includes(d.id)}
                    onChange={() =>
                      handleCheckboxChange(
                        d.id,
                        selectedDesignations,
                        setSelectedDesignations,
                      )
                    }
                  />
                  {/* designationCounts keyed by designation_id — matches d.id */}
                  {d.name} ({designationCounts[d.id] || 0})
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="dcr-leave-type-leave-type-section">
          <label className="dcr-leave-type-label-checkbox">
            <span>Types</span>
          </label>

          <div className="dcr-leave-type-leave-type-list grid grid-cols-3 gap-4">
            {leaveTypes
              .filter((type) => {
                if (isCoordinator) {
                  const hiddenTypes = ["Unlock Expense", "Present", "Sunday"];
                  return !hiddenTypes.includes(type.name);
                }

                if (isExpenseUser) {
                  return type.name === "Unlock Expense";
                }
                return true;
              })
              .map((type) => (
                <div
                  key={type.id}
                  className="border border-gray-300 p-2 rounded flex items-center gap-2 bg-gray-50"
                >
                  <input
                    type="checkbox"
                    checked={leaveType === type.name}
                    onChange={() =>
                      setLeaveType(leaveType === type.name ? "" : type.name)
                    }
                  />
                  {type.name}
                </div>
              ))}
          </div>
        </div>

        <div className="dcr-leave-type-employee-section">
          <label className="dcr-leave-type-label-checkbox">
            <span>Employees ({filteredEmployees.length})</span>
            <input
              type="checkbox"
              checked={
                filteredEmployees.length > 0 &&
                selectedEmployees.length === filteredEmployees.length
              }
              onChange={() =>
                handleSelectAll(
                  filteredEmployees,
                  selectedEmployees,
                  setSelectedEmployees,
                  "employee",
                )
              }
            />
          </label>
          <div className="dcr-leave-type-employee-list">
            {filteredEmployees.length > 0 ? (
              filteredEmployees.map((emp) => (
                <div key={emp.id} className="dcr-leave-type-checkbox-item">
                  <input
                    type="checkbox"
                    checked={selectedEmployees.includes(emp.id)}
                    onChange={() =>
                      handleCheckboxChange(
                        emp.id,
                        selectedEmployees,
                        setSelectedEmployees,
                      )
                    }
                  />
                  {`${emp.employee}-${emp.first_name} ${emp.last_name} (${emp.city_name}|${emp.designation}|${emp.division})`}
                </div>
              ))
            ) : (
              <div className="dcr-leave-type-no-employees">
                Please select Division, State, and Designation to view
                employees.
              </div>
            )}
          </div>
        </div>

        <div className="dcr-leave-type-form-group mt-5">
          <label className="block mb-3 font-medium">Mode</label>
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="leaveMode"
                value="single"
                checked={leaveMode === "single"}
                onChange={() => setLeaveMode("single")}
              />
              <span
                className={`leave-toggle-label ${
                  leaveMode === "single" ? "active" : ""
                }`}
              >
                Single
              </span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="leaveMode"
                value="multiple"
                checked={leaveMode === "multiple"}
                onChange={() => setLeaveMode("multiple")}
              />
              <span
                className={`leave-toggle-label ${
                  leaveMode === "multiple" ? "active" : ""
                }`}
              >
                Multiple
              </span>
            </label>
          </div>
        </div>

        <div className="dcr-leave-type-form-row">
          <div className="dcr-leave-type-form-group">
            <label>Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="dcr-leave-type-form-group">
            <label>End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              disabled={leaveMode === "single"}
              min={startDate}
              className={
                leaveMode === "single" ? "opacity-50 cursor-not-allowed" : ""
              }
            />
          </div>

          <div className="dcr-leave-type-form-group">
            <label>Message</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows="3"
            />
          </div>
        </div>

        <div className="mt-8 bg-gradient-to-r from-gray-50 to-gray-100 p-6 rounded-2xl shadow-inner">
          <div className="flex justify-center">
            <div className="flex bg-white p-1 rounded-full shadow-lg border border-gray-200 w-80">
              <button
                onClick={() => setActionMode("apply")}
                className={`w-1/2 py-3 rounded-full text-sm font-semibold transition-all duration-300 ${
                  actionMode === "apply"
                    ? "bg-blue-600 text-white shadow-md scale-105"
                    : "text-gray-600 hover:text-blue-600"
                }`}
              >
                Apply All
              </button>

              <button
                onClick={() => setActionMode("delete")}
                className={`w-1/2 py-3 rounded-full text-sm font-semibold transition-all duration-300 ${
                  actionMode === "delete"
                    ? "bg-red-600 text-white shadow-md scale-105"
                    : "text-gray-600 hover:text-red-600"
                }`}
              >
                Delete All
              </button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-center gap-4 mt-3">
            <button
              onClick={handleSubmit}
              className="px-8 py-3 rounded-xl bg-green-600 hover:bg-green-700 text-white font-semibold shadow-md transition-all duration-300 hover:scale-105"
            >
              Submit
            </button>

            <button
              onClick={() => navigate(`/dashboard`)}
              className="px-8 py-3 rounded-xl bg-gray-500 hover:bg-gray-600 text-white font-semibold shadow-md transition-all duration-300 hover:scale-105"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      <div className="mt-2">
        <LeavesHistory refreshKey={historyRefreshKey} />
      </div>

      {/* Skipped Details Modal */}
      {showSkippedModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white w-11/12 max-w-4xl rounded-2xl shadow-2xl p-6 relative animate-fadeIn">
            <button
              onClick={() => setShowSkippedModal(false)}
              className="absolute top-3 right-4 text-gray-500 hover:text-red-600 text-xl font-bold"
            >
              ✕
            </button>

            <h2 className="text-2xl font-bold text-center text-yellow-600 mb-6">
              Skipped Leave Details
            </h2>

            <div className="overflow-x-auto max-h-96 overflow-y-auto rounded-lg border">
              <table className="min-w-full text-sm text-left">
                <thead className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white">
                  <tr>
                    <th className="px-4 py-3">Employee ID</th>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Reason</th>
                  </tr>
                </thead>

                <tbody>
                  {applyResponse?.skipped?.map((item, index) => (
                    <tr
                      key={index}
                      className="border-b hover:bg-yellow-50 transition"
                    >
                      <td className="px-4 py-3 font-medium">{item.employee}</td>
                      <td className="px-4 py-3">{item.date}</td>
                      <td className="px-4 py-3 text-yellow-600 font-medium">
                        {item.reason}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-center mt-6">
              <button
                onClick={() => setShowSkippedModal(false)}
                className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg shadow-md transition-all duration-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Details Modal */}
      {showErrorModal && errorDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white w-11/12 max-w-4xl rounded-2xl shadow-2xl p-6 relative animate-fadeIn">
            <button
              onClick={() => setShowErrorModal(false)}
              className="absolute top-3 right-4 text-gray-500 hover:text-red-600 text-xl font-bold"
            >
              ✕
            </button>

            <h2 className="text-2xl font-bold text-center text-red-600 mb-2">
              Error Details
            </h2>
            <p className="text-center text-gray-600 mb-6">
              Status: {errorDetails.status || "Unknown"}
            </p>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-red-700 font-medium">{errorDetails.message}</p>
            </div>

            <div className="overflow-x-auto max-h-96 overflow-y-auto rounded-lg border">
              <table className="min-w-full text-sm text-left">
                <thead className="bg-gradient-to-r from-red-500 to-red-600 text-white">
                  <tr>
                    <th className="px-4 py-3">Field</th>
                    <th className="px-4 py-3">Error</th>
                  </tr>
                </thead>

                <tbody>
                  {Array.isArray(errorDetails.errors)
                    ? errorDetails.errors.map((error, index) => (
                        <tr
                          key={index}
                          className="border-b hover:bg-red-50 transition"
                        >
                          <td className="px-4 py-3 font-medium">
                            {error.field || "N/A"}
                          </td>
                          <td className="px-4 py-3 text-red-600">
                            {error.message || JSON.stringify(error)}
                          </td>
                        </tr>
                      ))
                    : Object.entries(errorDetails.errors || {}).map(
                        ([key, value], index) => (
                          <tr
                            key={index}
                            className="border-b hover:bg-red-50 transition"
                          >
                            <td className="px-4 py-3 font-medium">{key}</td>
                            <td className="px-4 py-3 text-red-600">
                              {Array.isArray(value)
                                ? value.join(", ")
                                : String(value)}
                            </td>
                          </tr>
                        ),
                      )}
                </tbody>
              </table>
            </div>

            <div className="flex justify-center mt-6 gap-3">
              <button
                onClick={() => setShowErrorModal(false)}
                className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg shadow-md transition-all duration-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DCRLeaveTypePage;
